import { useEffect, useRef, useState } from 'react';
import * as FinanceiroAPI from '../../../../../../api/financeiro';
import { booleanoFinanceiro, centavosDigitados, sugerirParcelas } from '../../../../../../utils/movimento/preFinanceiro';

// Apenas o principal automático. As rotinas com cálculo próprio continuam separadas.
export function permitePreviaParcelas(dados = {}, operacao = {}, parametros = {}) {
    return (
        booleanoFinanceiro(parametros.geraFinanceiro) &&
        ['Pagar', 'Receber'].includes(parametros.tipo) &&
        operacao.sigla !== 'LIM' &&
        !booleanoFinanceiro(operacao.balanco) &&
        !booleanoFinanceiro(operacao.industria) &&
        !booleanoFinanceiro(dados.transformado) &&
        !Object.prototype.hasOwnProperty.call(dados, 'preFinanceiro') &&
        dados.preFinanceiroConfiguracao == null &&
        !booleanoFinanceiro(dados.custo) &&
        !booleanoFinanceiro(dados.custoFilial) &&
        !dados.cobrancaCondominialId &&
        !booleanoFinanceiro(parametros.almocoColaboradorGeraFinanceiro) &&
        String(dados.Situacao?.descricao ?? dados.situacao ?? '').toLowerCase() !== 'cancelado'
    );
}

export function chavePreviaParcelas(dados = {}, operacao = {}, parametros = {}) {
    return JSON.stringify({
        dados: [
            'id', 'uuid', 'empresaId', 'operacaoId', 'parceiroId', 'data', 'situacaoId',
            'formaId', 'prazoId', 'contaId', 'totalValor', 'primeiraDataVencimento',
            'fixaDiaVencimento', 'diaVencimento', 'descontoFinanceiro', 'descontoFinanceiroPercentual',
            'obsLancamento', 'updatedAt', 'transformado', 'custo', 'custoFilial', 'cobrancaCondominialId',
        ].map((campo) => dados[campo] ?? null),
        itens: (Array.isArray(dados.movimentoItems) ? dados.movimentoItems : []).map((item) => [
            item.id, item.uuid, item.produtoId, item.pedido, item.quantidade, item.precoNegociado,
            item.valorUnitario, item.valorTotal, item.descontoValor, item.descontoFinanceiro,
        ]),
        contexto: [operacao.id, operacao.sigla, operacao.balanco, operacao.industria,
            parametros.geraFinanceiro, parametros.tipo, parametros.almocoColaboradorGeraFinanceiro,
            dados.Situacao?.descricao, dados.situacao,
            Object.prototype.hasOwnProperty.call(dados, 'preFinanceiro'), dados.preFinanceiroConfiguracao != null],
    });
}

export function montarPreviaParcelas(dados, forma, prazo) {
    if (!forma || !prazo || String(forma.id) !== String(dados.formaId) || String(prazo.id) !== String(dados.prazoId)) {
        throw new Error('Não foi possível conferir a forma e o prazo selecionados.');
    }
    const parcelas = sugerirParcelas({
        total: dados.totalValor,
        quantidade: prazo.parcela,
        primeiraDataVencimento: String(dados.primeiraDataVencimento || '').slice(0, 10),
        diasEntreParcelas: prazo.diasEntreParcelas,
        credito: forma.credito,
        fixaDiaVencimento: dados.fixaDiaVencimento,
        diaVencimento: dados.diaVencimento,
    }).map((parcela, indice, lista) => {
        const valor = Number(centavosDigitados(parcela.valor)) / 100;
        return {
            uuid: `previa-${indice + 1}`,
            parcela: indice + 1,
            parcelas: `${indice + 1}/${lista.length}`,
            dataVencimento: parcela.dataVencimento,
            valor,
            valorBaixado: 0,
            valorAberto: valor,
            codBarraPIX: parcela.codBarraPIX ?? '',
            historico: dados.obsLancamento ?? '',
            pagamentoForma: { descricao: forma.descricao ?? '' },
            pagamentoPrazo: { descricao: prazo.descricao ?? '' },
        };
    });
    return { parcelas, total: Number(centavosDigitados(dados.totalValor)) / 100 };
}

export default function usePreviaParcelasMovimento({ dados, operacao, parametros, bloqueado = false }) {
    const [resultado, setResultado] = useState(null);
    const [mostrarPrevia, setMostrarPrevia] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [erro, setErro] = useState('');
    const vivo = useRef(true);
    const requisicao = useRef(0);
    const disponivel = permitePreviaParcelas(dados, operacao, parametros);
    const chave = chavePreviaParcelas(dados, operacao, parametros);
    const contexto = useRef(null);
    contexto.current = { chave, disponivel, bloqueado };

    useEffect(() => {
        vivo.current = true;
        return () => { vivo.current = false; requisicao.current += 1; };
    }, []);

    useEffect(() => {
        requisicao.current += 1;
        setOcupado(false);
        setErro('');
        setMostrarPrevia(false);
        setResultado((anterior) => anterior ? { ...anterior, desatualizada: true } : null);
    }, [chave, bloqueado]);

    async function simular() {
        if (!disponivel || bloqueado || ocupado) return;
        const idRequisicao = ++requisicao.current;
        setOcupado(true);
        setErro('');
        const aindaAtual = () => vivo.current && idRequisicao === requisicao.current &&
            contexto.current.chave === chave && contexto.current.disponivel && !contexto.current.bloqueado;
        try {
            if (!Number(dados.formaId) || !Number(dados.prazoId)) {
                throw new Error('Selecione a forma e o prazo antes de simular as parcelas.');
            }
            const [forma, prazo] = await Promise.all([
                FinanceiroAPI.buscaFormaById(dados.formaId),
                FinanceiroAPI.buscaPrazoById(dados.prazoId),
            ]);
            if (!aindaAtual()) return;
            const previa = montarPreviaParcelas(dados, forma, prazo);
            setResultado({ ...previa, chave, desatualizada: false });
            setMostrarPrevia(true);
        } catch (error) {
            if (aindaAtual()) {
                setErro(error.message || 'Não foi possível simular as parcelas.');
                setMostrarPrevia(false);
            }
        } finally {
            if (aindaAtual()) setOcupado(false);
        }
    }

    const desatualizada = !!resultado && (resultado.desatualizada || resultado.chave !== chave);
    const visivel = disponivel && !bloqueado && mostrarPrevia && !!resultado && !desatualizada;
    return {
        disponivel, visivel, ocupado, erro, desatualizada, simular,
        parcelas: visivel ? resultado.parcelas : [],
        total: visivel ? resultado.total : 0,
        mostrarGravados: () => setMostrarPrevia(false),
    };
}
